
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Printer, BookOpen, Palette, Puzzle, Star } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { extractOriginalFileName } from "@/utils/fileOperations";

type Activity = Tables<"activities"> & {
  activity_categories?: {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
  } | null;
};

interface PublicActivityCardProps {
  activity: Activity;
}

const getIconComponent = (iconName: string | null) => {
  const iconMap: Record<string, React.ComponentType<any>> = {
    'palette': Palette,
    'puzzle': Puzzle,
    'star': Star,
    'book-open': BookOpen,
    'download': Download,
  };
  
  return iconMap[iconName || 'book-open'] || BookOpen;
};

export const PublicActivityCard = ({ activity }: PublicActivityCardProps) => {
  const categoryName = activity.activity_categories?.name || activity.type;
  const categoryColor = activity.activity_categories?.color || activity.color || '#6B7280';
  const IconComponent = getIconComponent(activity.activity_categories?.icon || activity.icon);

  const handleDownload = async () => {
    if (!activity.download_url) return;

    try {
      // Check if it's a storage file (starts with /) vs external URL (starts with http)
      if (activity.download_url.startsWith('/')) {
        // Handle Supabase storage files - remove leading slash for storage path
        const storagePath = activity.download_url.substring(1);
        
        const { data } = await supabase.storage
          .from('activities')
          .createSignedUrl(storagePath, 60 * 60); // 1 hour expiry

        if (data?.signedUrl) {
          // Extract original filename for download attribute
          const originalFileName = extractOriginalFileName(storagePath) || activity.title;
          
          // Force download by fetching the file and creating a blob
          const response = await fetch(data.signedUrl);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.download = originalFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up the blob URL
          window.URL.revokeObjectURL(url);
        } else {
          console.error('Failed to create signed URL for file:', storagePath);
        }
      } else {
        // Handle external URLs - open in new tab for download
        window.open(activity.download_url, '_blank');
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handlePrint = async () => {
    if (!activity.download_url) return;

    try {
      // Check if it's a storage file vs external URL
      if (activity.download_url.startsWith('/')) {
        // Handle Supabase storage files - remove leading slash for storage path
        const storagePath = activity.download_url.substring(1);
        
        const { data } = await supabase.storage
          .from('activities')
          .createSignedUrl(storagePath, 60 * 60);

        if (data?.signedUrl) {
          // Open in new tab for printing
          window.open(data.signedUrl, '_blank');
        } else {
          console.error('Failed to create signed URL for file:', storagePath);
        }
      } else {
        // Handle external URLs - open in new tab for printing
        window.open(activity.download_url, '_blank');
      }
    } catch (error) {
      console.error('Print failed:', error);
    }
  };

  return (
    <Card className={`overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-white/80 backdrop-blur-sm border-2`} 
          style={{ borderColor: `${categoryColor}33` }}>
      <CardHeader>
        <div className="flex items-center justify-between mb-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" 
               style={{ backgroundColor: `${categoryColor}33` }}>
            <IconComponent className="h-6 w-6" style={{ color: categoryColor }} />
          </div>
          <span className="px-3 py-1 text-sm font-semibold rounded-full text-white"
                style={{ backgroundColor: `${categoryColor}1A`, color: categoryColor }}>
            {categoryName}
          </span>
        </div>
        <CardTitle className="text-xl" style={{ color: categoryColor }}>
          {activity.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-gray-600 mb-4">{activity.description}</p>
        
        <div className="space-y-2 mb-6">
          {activity.age_group && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Age Group:</span>
              <span className="font-semibold text-gray-700">{activity.age_group}</span>
            </div>
          )}
          {activity.difficulty && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Difficulty:</span>
              <span className="font-semibold text-gray-700 capitalize">{activity.difficulty}</span>
            </div>
          )}
        </div>

        {activity.download_url && (
          <div className="flex gap-2">
            <Button 
              size="sm" 
              className="flex-1 text-white hover:opacity-90"
              style={{ backgroundColor: categoryColor }}
              onClick={handleDownload}
            >
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="hover:text-white"
              style={{ 
                borderColor: categoryColor, 
                color: categoryColor,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = categoryColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onClick={handlePrint}
            >
              <Printer className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
